import { toast } from '@heidi/design-system/toast';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useIntl } from 'react-intl';

import { sharedAgentMessages } from '../../shared.messages';
import type { ResolveInlineApproval } from '../projects/components/inline-approval/useInlineApprovalResolve';
import { useInlineApprovalResolve } from '../projects/components/inline-approval/useInlineApprovalResolve';
import {
  readCreatePatientProfileApproval,
  readPatientContextApproval,
  readPatientProfileId,
  type PatientContextCurrent,
} from './patient-context-approval';
import { PatientContextApprovalCard } from './patient-context-approval-card';
import { patientContextApprovalMessages as m } from './patient-context-approval.messages';

interface ProjectScope {
  kind?: string | null;
  patient_profile_id?: string | null;
}

export interface PatientContextApprovalPorts {
  useProject: (projectId: string | undefined) => {
    data?: { data_scope?: ProjectScope | null } | null;
    isLoading: boolean;
  };
  useAuth: () => { isAuthenticated: boolean; isLoading: boolean };
  useProfile: (
    patientProfileId: string | undefined,
    options: { enabled?: boolean; isAuthenticated?: boolean }
  ) => {
    data?: PatientContextCurrent | null;
    isLoading: boolean;
  };
}

interface CreatePatientProfileApprovalRequestProps {
  toolInput: Record<string, unknown> | undefined;
  approvalId: string;
  onResolve: ResolveInlineApproval;
  fallback: ReactNode;
}

export const CreatePatientProfileApprovalRequest = ({
  toolInput,
  approvalId,
  onResolve,
  fallback,
}: CreatePatientProfileApprovalRequestProps) => {
  const intl = useIntl();
  const view = readCreatePatientProfileApproval(intl, toolInput);
  const { pendingVerdict, isBusy, submit } = useInlineApprovalResolve({
    approvalId,
    onResolve,
  });

  if (!view) return fallback;

  return (
    <PatientContextApprovalCard
      {...view}
      showBefore={false}
      onApprove={() => submit('approve')}
      onDeny={() => submit('deny')}
      pendingVerdict={pendingVerdict}
      disabled={isBusy}
      data-testid="create-patient-profile-approval"
    />
  );
};

interface PatientContextApprovalRequestProps extends PatientContextApprovalPorts {
  toolInput: Record<string, unknown> | undefined;
  projectId: string | undefined;
  approvalId: string;
  onResolve: ResolveInlineApproval;
  fallback: ReactNode;
}

export const PatientContextApprovalRequest = ({
  toolInput,
  projectId,
  approvalId,
  onResolve,
  fallback,
  useProject,
  useAuth,
  useProfile,
}: PatientContextApprovalRequestProps) => {
  const intl = useIntl();
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const projectQuery = useProject(projectId);
  const inputId = readPatientProfileId(toolInput);
  const waitingForProject = !inputId && Boolean(projectId) && projectQuery.isLoading;
  const profileId = waitingForProject
    ? undefined
    : readPatientProfileId(toolInput, projectQuery.data?.data_scope);
  const profileQuery = useProfile(profileId, {
    isAuthenticated,
    enabled: Boolean(profileId),
  });
  const inFlight =
    waitingForProject || (Boolean(profileId) && (authLoading || profileQuery.isLoading));
  // Data, not isError: a failed background refetch keeps the profile the
  // clinician is already reading, and must not refuse the change.
  const profile = profileQuery.data ?? null;
  const hasProfile = profile !== null;
  const view = readPatientContextApproval(intl, toolInput, profile);
  const { pendingVerdict, isBusy, submit } = useInlineApprovalResolve({
    approvalId,
    onResolve,
  });
  const shouldRefuse = Boolean(view) && !inFlight && !hasProfile;
  const refused = useRef(false);
  const [refuseFailed, setRefuseFailed] = useState(false);

  useEffect(() => {
    if (!shouldRefuse || refused.current) return;
    refused.current = true;
    void onResolve(
      approvalId,
      'deny',
      intl.formatMessage(m.profileUnavailableRefusal),
      null
    ).catch(() => {
      setRefuseFailed(true);
      toast({
        title: intl.formatMessage(sharedAgentMessages.couldntResolve),
        tone: 'destructive',
      });
    });
  }, [approvalId, intl, onResolve, shouldRefuse]);

  if (!view) return fallback;
  if (shouldRefuse && !refuseFailed) return null;

  return (
    <PatientContextApprovalCard
      {...view}
      onApprove={() => submit('approve')}
      onDeny={() => submit('deny')}
      pendingVerdict={pendingVerdict}
      disabled={isBusy}
      approveDisabled={!hasProfile}
      detailsPending={inFlight}
    />
  );
};
